// src/components/user/UserHelpCenter.jsx
import React, { useState, useEffect, useMemo, useCallback } from "react";
import {
  Box, Container, Typography, Paper, Grid,
  TextField, IconButton, Chip, InputAdornment,
  Divider, Dialog, DialogContent, DialogTitle,
  FormControl, InputLabel, Select, MenuItem,
  Accordion, AccordionSummary, AccordionDetails,
  Tabs, Tab, Fade, Skeleton,
  Card, CardContent, CardActions, Button,
  Alert, Snackbar,
} from "@mui/material";

import SearchIcon        from "@mui/icons-material/Search";
import ClearIcon         from "@mui/icons-material/Clear";
import QuizIcon          from "@mui/icons-material/Quiz";
import ArticleIcon       from "@mui/icons-material/Article";
import ExpandMoreIcon    from "@mui/icons-material/ExpandMore";
import CloseIcon         from "@mui/icons-material/Close";
import HelpOutlineIcon   from "@mui/icons-material/HelpOutline";
import LocalOfferIcon    from "@mui/icons-material/LocalOffer";
import CalendarTodayIcon from "@mui/icons-material/CalendarToday";
import OpenInNewIcon     from "@mui/icons-material/OpenInNew";

import { useAuth }         from "../../contexts/AuthContext";
import { kbFaqService }     from "../../services/KbFaqService";
import { kbArticleService } from "../../services/KbArticleService";

// ─── Helpers ────────────────────────────────────────────────────────────────
const unwrap = (doc) => doc?.details || doc || {};

const flatten = (categories, itemsKey) => {
  const out = [];
  (categories || []).forEach((cat) => {
    const src = unwrap(cat);
    const items = Array.isArray(src[itemsKey]) ? src[itemsKey] : [];
    items.forEach((item) => {
      out.push({ ...item, categoryId: cat.id, category: src.name || "" });
    });
  });
  return out;
};

// End users only see "general" audience categories
const AUDIENCE = "general";

const fmtDate = (val) => {
  if (!val) return "—";
  try { return new Date(val).toLocaleDateString(); }
  catch { return "—"; }
};

const itemCreatedAt = (item) =>
  item?.createdAt || item?.metadata?.createdAt || null;
const itemUpdatedAt = (item) =>
  item?.updatedAt || item?.metadata?.updatedAt || null;

// ─── Skeleton (unchanged) ───────────────────────────────────────────────────
const HelpCenterSkeleton = () => (
  <Container maxWidth="xl" sx={{ mt: 4, mb: 4, display: "flex", flexDirection: "column", gap: 3 }}>
    <Box>
      <Skeleton variant="text" width={260} height={44} />
      <Skeleton variant="text" width={380} height={20} />
    </Box>
    <Box sx={{ borderRadius: 3, border: "1px solid #e0e0e0", background: "#fff", overflow: "hidden" }}>
      <Box sx={{ display: "flex", gap: 2, px: 2, py: 1, borderBottom: "1px solid #e0e0e0" }}>
        <Skeleton variant="rounded" width={200} height={36} />
        <Skeleton variant="rounded" width={160} height={36} />
      </Box>
      <Box sx={{ p: 2.5 }}>
        <Box sx={{ display: "flex", gap: 2 }}>
          <Skeleton variant="rounded" width="66%" height={40} />
          <Skeleton variant="rounded" width="33%" height={40} />
        </Box>
      </Box>
      <Box sx={{ borderTop: "1px solid #e0e0e0" }}>
        {Array.from({ length: 5 }, (_, i) => (
          <Box key={i} sx={{
            display: "flex", alignItems: "center", justifyContent: "space-between",
            px: 3, py: 2, borderBottom: "1px solid #f5f5f5",
          }}>
            <Box sx={{ flex: 1 }}>
              <Skeleton variant="text" width="60%" height={18} sx={{ mb: 0.75 }} />
              <Box sx={{ display: "flex", gap: 0.75 }}>
                <Skeleton variant="rounded" width={80} height={22} />
                <Skeleton variant="rounded" width={60} height={22} />
                <Skeleton variant="rounded" width={70} height={22} />
              </Box>
            </Box>
          </Box>
        ))}
      </Box>
    </Box>
  </Container>
);

// ─── Article Detail Dialog (unchanged structure, date-guarded) ──────────────
const ArticleDetailDialog = ({ article, open, onClose }) => {
  if (!article) return null;
  return (
    <Dialog open={open} onClose={onClose} maxWidth="md" fullWidth TransitionComponent={Fade}>
      <DialogTitle>
        <Box sx={{ display: "flex", justifyContent: "space-between", alignItems: "flex-start", gap: 1 }}>
          <Typography variant="h6" fontWeight={700} sx={{ flex: 1 }}>
            {article.title}
          </Typography>
          <IconButton onClick={onClose} size="small" sx={{ mt: -0.5 }}>
            <CloseIcon />
          </IconButton>
        </Box>
        <Box sx={{ display: "flex", flexWrap: "wrap", gap: 0.75, mt: 1 }}>
          <Chip label={article.category} size="small" color="success"
            sx={{ fontWeight: 600, fontSize: "0.7rem" }} />
          {article.tags?.map((tag, i) => (
            <Chip key={i} label={tag} size="small" variant="outlined"
              sx={{ fontSize: "0.68rem" }} />
          ))}
        </Box>
      </DialogTitle>
      <Divider />
      <DialogContent sx={{ pt: 3 }}>
        <Typography variant="body2" color="text.secondary"
          sx={{ whiteSpace: "pre-line", lineHeight: 1.8, fontSize: "0.95rem" }}>
          {article.content}
        </Typography>
        <Divider sx={{ my: 3 }} />
        <Box sx={{ display: "flex", gap: 3, flexWrap: "wrap" }}>
          <Box sx={{ display: "flex", alignItems: "center", gap: 0.5 }}>
            <CalendarTodayIcon sx={{ fontSize: 14, color: "text.disabled" }} />
            <Typography variant="caption" color="text.disabled">
              Created: {fmtDate(itemCreatedAt(article))}
            </Typography>
          </Box>
          {itemUpdatedAt(article) && (
            <Box sx={{ display: "flex", alignItems: "center", gap: 0.5 }}>
              <CalendarTodayIcon sx={{ fontSize: 14, color: "text.disabled" }} />
              <Typography variant="caption" color="text.disabled">
                Updated: {fmtDate(itemUpdatedAt(article))}
              </Typography>
            </Box>
          )}
        </Box>
      </DialogContent>
    </Dialog>
  );
};

// ─── Main Component ─────────────────────────────────────────────────────────
const UserHelpCenter = () => {
  const { currentUser } = useAuth();

  // UI state
  const [activeTab,         setActiveTab]         = useState("faqs");
  const [searchQuery,       setSearchQuery]       = useState("");
  const [selectedCategory,  setSelectedCategory]  = useState("");
  const [selectedArticle,   setSelectedArticle]   = useState(null);
  const [showArticleDialog, setShowArticleDialog] = useState(false);
  const [loading,           setLoading]           = useState(true);
  const [alert,             setAlert]             = useState({ show: false, type: "info", message: "" });

  // Data state
  const [faqs,              setFaqs]              = useState([]);
  const [articles,          setArticles]          = useState([]);
  const [faqCategories,     setFaqCategories]     = useState([]);
  const [articleCategories, setArticleCategories] = useState([]);

  // ── Bootstrap ──────────────────────────────────────────────────────────────
  useEffect(() => { loadData(); }, [currentUser]);

  // ── loadData — repo via services ───────────────────────────────────────────
  const loadData = useCallback(async () => {
    setLoading(true);
    try {
      const [faqCats, artCats] = await Promise.all([
        kbFaqService.fetchCategories({ audience: AUDIENCE }),
        kbArticleService.fetchCategories({ audience: AUDIENCE }),
      ]);

      setFaqCategories(faqCats || []);
      setArticleCategories(artCats || []);
      setFaqs(flatten(faqCats, "questions"));
      setArticles(flatten(artCats, "articles"));
    } catch (err) {
      console.error("Failed to load Help Center:", err);
      setAlert({
        show: true,
        type: "error",
        message: "Failed to load Help Center content. Please try again.",
      });
    } finally {
      setLoading(false);
    }
  }, []);

  // ── Derived, filtered data (no separate filtered* state needed) ────────────
  const filteredFaqs = useMemo(() => {
    const q = searchQuery.trim().toLowerCase();
    return faqs.filter((faq) => {
      const matchesSearch =
        !q ||
        (faq.question || "").toLowerCase().includes(q) ||
        (faq.answer || "").toLowerCase().includes(q) ||
        (faq.tags || []).some((t) => t.toLowerCase().includes(q));
      const matchesCategory = !selectedCategory || faq.categoryId === selectedCategory;
      return matchesSearch && matchesCategory;
    });
  }, [faqs, searchQuery, selectedCategory]);

  const filteredArticles = useMemo(() => {
    const q = searchQuery.trim().toLowerCase();
    return articles.filter((a) => {
      const matchesSearch =
        !q ||
        (a.title || "").toLowerCase().includes(q) ||
        (a.content || "").toLowerCase().includes(q) ||
        (a.tags || []).some((t) => t.toLowerCase().includes(q));
      const matchesCategory = !selectedCategory || a.categoryId === selectedCategory;
      return matchesSearch && matchesCategory;
    });
  }, [articles, searchQuery, selectedCategory]);

  // ── Helpers ────────────────────────────────────────────────────────────────
  const getCurrentData = () =>
    activeTab === "faqs" ? filteredFaqs : filteredArticles;

  const getCurrentCategories = () =>
    activeTab === "faqs" ? faqCategories : articleCategories;

  const clearFilters = () => {
    setSearchQuery("");
    setSelectedCategory("");
  };

  const openArticle  = (a) => { setSelectedArticle(a); setShowArticleDialog(true); };
  const closeArticle = ()  => { setShowArticleDialog(false); setSelectedArticle(null); };

  const faqCount     = filteredFaqs.length;
  const articleCount = filteredArticles.length;

  if (loading) return <HelpCenterSkeleton />;

  // ── Render ─────────────────────────────────────────────────────────────────
  return (
    <Container maxWidth="xl" sx={{ mt: 4, mb: 4, display: "flex", flexDirection: "column", gap: 3 }}>

      {/* Header */}
      <Box>
        <Typography variant="h4" component="h1"
          sx={{ fontWeight: 700, color: "text.primary", letterSpacing: "-0.5px" }}>
          Help Center
        </Typography>
        <Typography variant="body2" color="text.secondary">
          Find answers to your questions and learn about the system
        </Typography>
      </Box>

      {/* Alert */}
      <Snackbar open={alert.show} autoHideDuration={5000}
        onClose={() => setAlert((p) => ({ ...p, show: false }))}
        anchorOrigin={{ vertical: "top", horizontal: "right" }}>
        <Alert severity={alert.type} onClose={() => setAlert((p) => ({ ...p, show: false }))}
          sx={{ fontWeight: 500 }}>
          {alert.message}
        </Alert>
      </Snackbar>

      {/* Main panel */}
      <Paper variant="outlined" sx={{ borderRadius: 3, overflow: "hidden" }}>
        <Tabs value={activeTab}
          onChange={(_, v) => { setActiveTab(v); setSelectedCategory(""); setSearchQuery(""); }}
          sx={{
            px: 2, bgcolor: "background.default",
            "& .MuiTab-root": { textTransform: "none", fontWeight: 600, minHeight: 52 },
          }}>
          <Tab value="faqs" icon={<QuizIcon />} iconPosition="start"
            label={`Frequently Asked Questions (${faqs.length})`} />
          <Tab value="articles" icon={<ArticleIcon />} iconPosition="start"
            label={`Knowledge Articles (${articles.length})`} />
        </Tabs>

        <Divider />

        {/* Search + filter */}
        <Box sx={{ p: 2.5 }}>
          <Grid container spacing={2} alignItems="center">
            <Grid item xs={12} md={8}>
              <TextField fullWidth size="small" variant="outlined"
                label={`Search ${activeTab === "faqs" ? "FAQs" : "articles"}...`}
                value={searchQuery}
                onChange={(e) => setSearchQuery(e.target.value)}
                InputProps={{
                  startAdornment: (
                    <InputAdornment position="start">
                      <SearchIcon sx={{ color: "action.active" }} fontSize="small" />
                    </InputAdornment>
                  ),
                  endAdornment: searchQuery && (
                    <InputAdornment position="end">
                      <IconButton size="small" onClick={() => setSearchQuery("")}>
                        <ClearIcon fontSize="small" />
                      </IconButton>
                    </InputAdornment>
                  ),
                }}
                sx={{ "& .MuiOutlinedInput-root": { borderRadius: 2 } }}
              />
            </Grid>
            <Grid item xs={12} md={4}>
              <FormControl fullWidth size="small">
                <InputLabel>All Categories</InputLabel>
                <Select value={selectedCategory} label="All Categories"
                  onChange={(e) => setSelectedCategory(e.target.value)}
                  sx={{ borderRadius: 2 }}>
                  <MenuItem value="">All Categories</MenuItem>
                  {getCurrentCategories().map((cat) => (
                    <MenuItem key={cat.id} value={cat.id}>
                      {unwrap(cat).name || cat.id}
                    </MenuItem>
                  ))}
                </Select>
              </FormControl>
            </Grid>
          </Grid>

          {searchQuery && (
            <Typography variant="body2" color="text.secondary" sx={{ mt: 1.5 }}>
              Found <strong>{activeTab === "faqs" ? faqCount : articleCount}</strong>{" "}
              {activeTab === "faqs" ? "FAQ" : "article"}
              {(activeTab === "faqs" ? faqCount : articleCount) !== 1 ? "s" : ""} matching "
              <em>{searchQuery}</em>"
              {selectedCategory && (
                <>
                  {" "}in{" "}
                  <strong>
                    {getCurrentCategories().find((c) => c.id === selectedCategory)?.name}
                  </strong>
                </>
              )}
            </Typography>
          )}
        </Box>

        <Divider />

        {/* Content */}
        <Box sx={{ p: 0 }}>
          {getCurrentData().length === 0 ? (
            <Box sx={{
              p: 8, textAlign: "center",
              display: "flex", flexDirection: "column", alignItems: "center", gap: 1.5,
            }}>
              {activeTab === "faqs"
                ? <HelpOutlineIcon sx={{ fontSize: 56, color: "action.disabled" }} />
                : <ArticleIcon      sx={{ fontSize: 56, color: "action.disabled" }} />}
              <Typography variant="h6" color="text.secondary" fontWeight={600}>
                No {activeTab === "faqs" ? "FAQs" : "articles"} found
              </Typography>
              <Typography variant="body2" color="text.disabled">
                {searchQuery || selectedCategory
                  ? "No items match your current filters."
                  : `No ${activeTab === "faqs" ? "FAQs" : "articles"} are available yet.`}
              </Typography>
              {(searchQuery || selectedCategory) && (
                <Button variant="contained" onClick={clearFilters}
                  sx={{ mt: 1, borderRadius: 2, textTransform: "none", fontWeight: 600 }}
                  disableElevation>
                  Clear Filters
                </Button>
              )}
            </Box>
          ) : activeTab === "faqs" ? (
            <Box>
              {getCurrentData().map((faq, index) => (
                <Fade in key={`${faq.categoryId}-${faq.id}`}>
                  <Accordion disableGutters elevation={0} square sx={{
                    borderBottom: index < getCurrentData().length - 1 ? "1px solid" : "none",
                    borderColor: "divider",
                    "&:before": { display: "none" },
                    "&.Mui-expanded": { margin: 0 },
                  }}>
                    <AccordionSummary expandIcon={<ExpandMoreIcon />} sx={{
                      px: 3, py: 1.5,
                      "&:hover": { bgcolor: "action.hover" },
                      "& .MuiAccordionSummary-content": {
                        mr: 1, alignItems: "center", justifyContent: "space-between",
                      },
                    }}>
                      <Box sx={{ flex: 1, minWidth: 0 }}>
                        <Typography variant="body2" fontWeight={700} color="text.primary" sx={{ mb: 0.5 }}>
                          {faq.question}
                        </Typography>
                        <Box sx={{ display: "flex", flexWrap: "wrap", gap: 0.5 }}>
                          <Chip label={faq.category} size="small" color="success"
                            sx={{ fontWeight: 600, fontSize: "0.7rem" }} />
                          {faq.tags?.map((tag, i) => (
                            <Chip key={i} label={tag} size="small" variant="outlined"
                              sx={{ fontSize: "0.68rem" }} />
                          ))}
                        </Box>
                      </Box>
                    </AccordionSummary>
                    <AccordionDetails sx={{ px: 3, pb: 2.5, pt: 0 }}>
                      <Divider sx={{ mb: 2 }} />
                      <Typography variant="body2" color="text.secondary"
                        sx={{ whiteSpace: "pre-line", lineHeight: 1.8, fontSize: "0.95rem" }}>
                        {faq.answer}
                      </Typography>
                      <Box sx={{ mt: 2, display: "flex", gap: 2, flexWrap: "wrap" }}>
                        <Typography variant="caption" color="text.disabled">
                          Created: {fmtDate(itemCreatedAt(faq))}
                        </Typography>
                        {itemUpdatedAt(faq) && (
                          <Typography variant="caption" color="text.disabled">
                            Updated: {fmtDate(itemUpdatedAt(faq))}
                          </Typography>
                        )}
                      </Box>
                    </AccordionDetails>
                  </Accordion>
                </Fade>
              ))}
            </Box>
          ) : (
            <Box sx={{ p: 3 }}>
              <Grid container spacing={2.5}>
                {getCurrentData().map((article) => (
                  <Grid item xs={12} sm={6} lg={4} key={`${article.categoryId}-${article.id}`}>
                    <Fade in>
                      <Card variant="outlined" sx={{
                        height: "100%", display: "flex", flexDirection: "column",
                        borderRadius: 2,
                        transition: "box-shadow 0.2s, border-color 0.2s",
                        "&:hover": { boxShadow: 3, borderColor: "primary.light" },
                      }}>
                        <CardContent sx={{ flex: 1 }}>
                          <Box sx={{ display: "flex", flexWrap: "wrap", gap: 0.5, mb: 1.5 }}>
                            <Chip label={article.category} size="small" color="success"
                              sx={{ fontWeight: 600, fontSize: "0.7rem" }} />
                            {article.tags?.slice(0, 2).map((tag, i) => (
                              <Chip key={i} label={tag} size="small" variant="outlined"
                                icon={<LocalOfferIcon sx={{ fontSize: "0.7rem !important" }} />}
                                sx={{ fontSize: "0.68rem" }} />
                            ))}
                            {article.tags?.length > 2 && (
                              <Chip label={`+${article.tags.length - 2}`} size="small"
                                variant="outlined" sx={{ fontSize: "0.68rem" }} />
                            )}
                          </Box>
                          <Typography variant="subtitle1" fontWeight={700} color="text.primary"
                            sx={{ mb: 1, lineHeight: 1.4 }}>
                            {article.title}
                          </Typography>
                          <Typography variant="body2" color="text.secondary" sx={{
                            lineHeight: 1.6,
                            display: "-webkit-box",
                            WebkitLineClamp: 3,
                            WebkitBoxOrient: "vertical",
                            overflow: "hidden",
                          }}>
                            {article.content}
                          </Typography>
                        </CardContent>
                        <Box sx={{ px: 2, pb: 0.5, display: "flex", alignItems: "center", gap: 0.5 }}>
                          <CalendarTodayIcon sx={{ fontSize: 12, color: "text.disabled" }} />
                          <Typography variant="caption" color="text.disabled">
                            {fmtDate(itemCreatedAt(article))}
                          </Typography>
                        </Box>
                        <Divider />
                        <CardActions sx={{ px: 2, py: 1.25 }}>
                          <Button size="small" variant="outlined"
                            endIcon={<OpenInNewIcon fontSize="small" />}
                            onClick={() => openArticle(article)}
                            sx={{ borderRadius: 1.5, textTransform: "none", fontWeight: 600, fontSize: "0.8rem" }}>
                            Read Article
                          </Button>
                        </CardActions>
                      </Card>
                    </Fade>
                  </Grid>
                ))}
              </Grid>
            </Box>
          )}
        </Box>
      </Paper>

      {/* Browse by category */}
      {!searchQuery && getCurrentData().length > 0 && (
        <Paper variant="outlined" sx={{ borderRadius: 3, overflow: "hidden" }}>
          <Box sx={{
            px: 3, py: 2, bgcolor: "background.default",
            borderBottom: "1px solid", borderColor: "divider",
          }}>
            <Typography variant="subtitle2" fontWeight={700} color="text.primary">
              Browse by Category
            </Typography>
          </Box>
          <Box sx={{ px: 3, py: 2, display: "flex", flexWrap: "wrap", gap: 1 }}>
            <Button size="small"
              variant={selectedCategory === "" ? "contained" : "outlined"}
              onClick={() => setSelectedCategory("")}
              disableElevation
              sx={{ borderRadius: 2, textTransform: "none", fontWeight: 600, fontSize: "0.8rem" }}>
              All ({activeTab === "faqs" ? faqs.length : articles.length})
            </Button>
            {getCurrentCategories().map((cat) => {
              const count =
                activeTab === "faqs"
                  ? faqs.filter((f) => f.categoryId === cat.id).length
                  : articles.filter((a) => a.categoryId === cat.id).length;
              return (
                <Button key={cat.id} size="small"
                  variant={selectedCategory === cat.id ? "contained" : "outlined"}
                  onClick={() => setSelectedCategory(selectedCategory === cat.id ? "" : cat.id)}
                  disableElevation
                  sx={{ borderRadius: 2, textTransform: "none", fontWeight: 600, fontSize: "0.8rem" }}>
                  {unwrap(cat).name || cat.id} ({count})
                </Button>
              );
            })}
          </Box>
        </Paper>
      )}

      {/* Article dialog */}
      <ArticleDetailDialog
        article={selectedArticle}
        open={showArticleDialog}
        onClose={closeArticle}
      />
    </Container>
  );
};

export default UserHelpCenter;